import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { useRefreshSession } from '@/api/bff/auth/use-refresh-session';
import {
  _postEntities,
  _submitEntities,
  EntityPayload,
  useUpdateEntity,
} from '@/api/entity/mutations';
import { _getSingleEntityOptions, ENTITY_QUERY_KEYS } from '@/api/entity/queries';
import { versionOption } from '@/api/entity/types';
import { _postFiles, FileMetadata } from '@/api/fund/mutations';
import { _getCountries, _getRegions } from '@/api/lookup/queries';
import { LocationList } from '@/api/lookup/types';
import { PagePaths } from '@/routes/page-paths';
import { COVER_IMAGE_ID } from '@/shared/constants/fund-documents';
import { Permission, usePermissions } from '@/shared/modules/permissions';
import { useEntitySlice, useSelectedUserConfigSlice } from '@/stores/selectors';
import type { StepForms } from '@/stores/slices/entity-slice';
import { emitToast } from '@/utils/emit-toast';
import { uploadToS3 } from '@/utils/upload-to-s3';
import { isImageFile } from '@/utils/validate-file-type';

import { useEntityReviewMode } from './use-entity-review-mode';
import { changedDirectorUserIds, computeEntityDiff } from './utils/compute-entity-diff';
import { isEntityInReapprovalState, shouldPersistOnNavigate } from './utils/entity-review-routing';
import { hasInvalidContactFormat, validateStep } from './validate-step';

const TOTAL_STEPS = 5;
const UNSAVED_ENTITY_DRAFT_KEY = 'unsaved-entity-draft';

/**
 * Creating an entity switches the session to the new entity's context, which remounts this page
 * while the route is still `/create-entity`. The reset below then wipes the wizard it has just
 * advanced to step 2. This records that a created entity is still being navigated to, so those
 * remounts leave the store alone; landing on `/create-entity/:id` ends it. Module scope because a
 * ref does not survive the remounts.
 */
let isNavigatingToCreatedEntity = false;

export const useCreateEntity = () => {
  const params = useParams();
  const [isStepTransitioning, setIsStepTransitioning] = useState(false);
  const { data: currentEntity, isLoading: isEntityLoading } = useQuery(
    _getSingleEntityOptions(params.id),
  );
  const { has, isLoading: arePermissionsUnresolved } = usePermissions();
  const canReviewCrossEntity = has(Permission.GlobalAdminCrossEntityAccess);
  const isReapproval = isEntityInReapprovalState({
    entityApprovalStatus: currentEntity?.entityApprovalStatus,
    draftStatus: currentEntity?.draftStatus,
  });
  const { data: publishedEntity, isFetched: isPublishedFetched } = useQuery({
    ..._getSingleEntityOptions(params.id, versionOption.Published),
    enabled: Boolean(params.id) && isReapproval && canReviewCrossEntity,
    retry: false,
  });
  const hasPublishedVersion = Boolean(publishedEntity);
  const navigate = useNavigate();
  const {
    currentStep,
    resetState,
    setStep,
    updateStepForm,
    stepForms,
    markStepValidated,
    hasUnsavedChanges,
    saveCurrentState,
    setIsLogoUploading,
    setEntityVersions,
  } = useEntitySlice();
  const { shareClasses } = stepForms.shareStructure;
  const { setSelectedEntityId, setSelectedUserRole } = useSelectedUserConfigSlice();
  const { isReviewMode, isOnReviewRoute, isViewingNewVersion, forms } = useEntityReviewMode();
  const { approvedDirectorUserIds } = useEntitySlice();
  const isEntityQuerySettled =
    !params.id ||
    (!arePermissionsUnresolved &&
      !isEntityLoading &&
      (!isReapproval || !canReviewCrossEntity || isPublishedFetched));

  const shouldLeaveReviewRoute = isOnReviewRoute && isEntityQuerySettled && !hasPublishedVersion;

  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === TOTAL_STEPS;
  const isEntityApprovalStatusDraft = currentEntity?.entityApprovalStatus === 'Draft';
  const isEntityApprovalStatusVerified = currentEntity?.entityApprovalStatus === 'Verified';

  const queryClient = useQueryClient();

  const { mutateAsync: postEntities } = useMutation({ mutationFn: _postEntities });
  const { mutateAsync: putEntities } = useUpdateEntity();
  const { mutateAsync: submitEntities } = useMutation({ mutationFn: _submitEntities });
  const { mutateAsync: refreshToken } = useRefreshSession();
  const { mutateAsync: postFile } = useMutation({
    mutationFn: _postFiles,
  });

  const { data: countries } = useQuery(
    _getCountries({ list: LocationList.INCORPORATION, includeRegions: false }),
  );
  const country = forms.country;

  const { data: regions, isLoading: isLoadingRegions } = useQuery(
    _getRegions(country as string, { list: LocationList.INCORPORATION }),
  );

  const filteredDirectors = [...forms.members, ...forms.invitations]
    .filter((el) => el.legalRole === 'Director')
    .filter(
      (el) =>
        isViewingNewVersion || ('userId' in el && approvedDirectorUserIds.includes(el.userId)),
    )
    .sort((a, b) => Date.parse(b.createdAt as string) - Date.parse(a.createdAt as string));

  const countrySelectOptions = useMemo(
    () =>
      (countries ?? []).map((c) => ({
        label: c.name,
        value: c.id,
        regionRequired: c.regionRequired,
      })),
    [countries],
  );

  const selectedCountry = useMemo(
    () => countrySelectOptions.find((c) => c.value === country),
    [country, countrySelectOptions],
  );

  const regionSelectOptions = useMemo(
    () => ({
      options: (regions ?? []).map((r) => ({ label: r.name, value: r.id })),
      required: !!selectedCountry?.regionRequired,
    }),
    [regions, selectedCountry],
  );

  const uploadFile = useCallback(
    async (file: File | null, entityId?: string | number | null) => {
      if (!file || !(await isImageFile(file))) {
        return;
      }

      setIsLogoUploading(true);
      try {
        const metadata = {
          filename: file.name,
          contentType: file.type,
          category: 'logo',
          ownerType: 'entity',
          ownerId: entityId ?? stepForms?.id,
          file,
        } as FileMetadata & { file: File };

        const data = await postFile(metadata);

        await uploadToS3({
          uploadUrl: data.uploadUrl,
          uploadHeaders: data.uploadHeaders as Record<string, string>,
          file,
          fileId: data.fileId,
          compress: true,
        });
        return data.fileId;
      } finally {
        setIsLogoUploading(false);
      }
    },
    [postFile, stepForms?.id, setIsLogoUploading],
  );

  const totalAllocated = useMemo(
    () =>
      shareClasses.reduce((total, shareClass) => {
        const list = shareClass.shareholders ?? [];
        return total + list.reduce((acc, s) => acc + Number(s.numberOfShares || 0), 0);
      }, 0),
    [shareClasses],
  );

  const nextHandlerClick = useCallback(
    async (isSaveOnly = false) => {
      if (isStepTransitioning) return;
      setIsStepTransitioning(true);
      try {
        markStepValidated(currentStep);

        const handleAfterSuccess = () => {
          if (!isSaveOnly) setStep(currentStep + 1);
        };

        const createEntityData: EntityPayload = {
          legalName: stepForms.legalName || null,
          displayName: stepForms.displayName || null,
          country: stepForms.country || null,
          state: stepForms.state || null,
          type: stepForms.type || null,
          identificationNumber: stepForms.identificationNumber || null,
          logoImageId: stepForms.logoImageId || COVER_IMAGE_ID,
          registeredAt: stepForms.registeredAt
            ? (stepForms.registeredAt as Date).toISOString()
            : null,
          entityEmail: stepForms.entityEmail?.trim() || null,
          entityWebsite: stepForms.entityWebsite?.trim() || null,
          registeredAddressCity: stepForms.registeredAddressCity || null,
          registeredAddress: stepForms.registeredAddress || null,
          registeredAddressPostalCode: stepForms.registeredAddressPostalCode || null,
        };

        const putEntityData = {
          ...createEntityData,
          shareStructure: {
            totalNumberOfShares: stepForms.shareStructure?.totalNumberOfShares ?? null,
            shareClasses: (stepForms.shareStructure?.shareClasses ?? []).map((sc) => ({
              class: sc.class ?? null,
              numberOfShares: sc.numberOfShares ?? null,
              shareholders: sc.shareholders?.length
                ? sc.shareholders.map((sh) => ({
                    type: sh.type ?? null,
                    email: sh.email?.trim() ?? null,
                    fullName: sh.fullName?.trim() ?? null,
                    numberOfShares: sh.numberOfShares ?? null,
                  }))
                : null,
            })),
          },
        };

        if (hasInvalidContactFormat(stepForms)) return;

        if (currentStep === 1) {
          if (params.id) {
            if (hasUnsavedChanges()) {
              await putEntities({ id: stepForms.id, body: putEntityData });
              if (isSaveOnly && isEntityApprovalStatusVerified) {
                emitToast({
                  title: 'Saved',
                  message: 'Your entity has been submitted and is re-verification pending.',
                  type: 'success',
                  position: 'top-right',
                });
              }
            }
          } else {
            const result = await postEntities(createEntityData).catch((error: unknown) => {
              console.error('Failed to create entity', error);
              return null;
            });
            const newId = result?.id;
            if (!newId) {
              emitToast({
                title: 'Could not create entity',
                message: 'Your details were not saved. Please try again.',
                type: 'error',
                position: 'top-right',
              });
              return;
            }
            isNavigatingToCreatedEntity = true;
            await refreshToken({ entityId: newId, currentSession: 'Entity' });
            await queryClient.invalidateQueries({ queryKey: [ENTITY_QUERY_KEYS.entities] });
            updateStepForm({ id: newId });
            setSelectedEntityId(String(newId));
            setSelectedUserRole('entity');
            handleAfterSuccess();
            setTimeout(() => navigate(`${PagePaths.createEntity}/${newId}`), 0);
            if (stepForms.previewLogo) {
              const fileId = await uploadFile(stepForms.previewLogo, newId);
              await putEntities({
                id: newId,
                body: { ...createEntityData, logoImageId: fileId },
              });
              updateStepForm({ logoImageId: fileId, previewLogo: null });
            }
            return;
          }
        }

        if (currentStep === 3) {
          if (hasUnsavedChanges()) {
            await putEntities({ id: stepForms.id, body: putEntityData });
            if (isSaveOnly && isEntityApprovalStatusVerified) {
              emitToast({
                title: 'Saved',
                message: 'Your entity has been submitted and is re-verification pending.',
                type: 'success',
              });
            }
          }
        }

        if (isLastStep) {
          return;
        }

        handleAfterSuccess();
        saveCurrentState();
      } finally {
        setIsStepTransitioning(false);
      }
    },
    [
      currentStep,
      hasUnsavedChanges,
      isEntityApprovalStatusVerified,
      isLastStep,
      isStepTransitioning,
      markStepValidated,
      navigate,
      params.id,
      postEntities,
      putEntities,
      queryClient,
      refreshToken,
      saveCurrentState,
      setSelectedEntityId,
      setSelectedUserRole,
      setStep,
      stepForms,
      updateStepForm,
      uploadFile,
    ],
  );

  const handleStepClick = useCallback(
    async (stepId: number) => {
      if (isStepTransitioning) return;
      setIsStepTransitioning(true);
      try {
        if (shouldPersistOnNavigate(isReviewMode)) {
          for (let step = 1; step < stepId; step += 1) {
            markStepValidated(step);
          }
          // Leaving step 1 would hide the field error and carry the value into every later save.
          if (hasInvalidContactFormat(stepForms)) return;
          await nextHandlerClick(true);
        }
        setStep(stepId);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } finally {
        setIsStepTransitioning(false);
      }
    },
    [nextHandlerClick, setStep, markStepValidated, isStepTransitioning, isReviewMode, stepForms],
  );

  const submit = async () => {
    await submitEntities(stepForms.id);
    emitToast({
      title: 'Submitted',
      message: 'Your entity has been submitted and is pending approval.',
      type: 'success',
      position: 'top-right',
    });
    resetState();
    setSelectedEntityId(stepForms.id);
    setSelectedUserRole('entity');
    navigate(PagePaths.allFunds);
  };

  const backHandlerClick = () => {
    if (currentStep > 1) {
      setStep(currentStep - 1);
      saveCurrentState();
    } else {
      navigate(PagePaths.overview);
    }
  };

  const autoSaveOnLeave = useCallback(async (): Promise<string | undefined> => {
    // The API rejects the whole draft over one malformed contact value, so there is nothing to save.
    if (!hasUnsavedChanges() || hasInvalidContactFormat(stepForms)) return;

    const createEntityData: EntityPayload = {
      legalName: stepForms.legalName || null,
      displayName: stepForms.displayName || null,
      country: stepForms.country || null,
      state: stepForms.state || null,
      type: stepForms.type || null,
      identificationNumber: stepForms.identificationNumber || null,
      logoImageId: stepForms.logoImageId || COVER_IMAGE_ID,
      registeredAt: stepForms.registeredAt ? (stepForms.registeredAt as Date).toISOString() : null,
      entityEmail: stepForms.entityEmail?.trim() || null,
      entityWebsite: stepForms.entityWebsite?.trim() || null,
      registeredAddressCity: stepForms.registeredAddressCity || null,
      registeredAddress: stepForms.registeredAddress || null,
      registeredAddressPostalCode: stepForms.registeredAddressPostalCode || null,
    };

    if (stepForms.id) {
      await putEntities({
        id: stepForms.id,
        body: {
          ...createEntityData,
          shareStructure: {
            totalNumberOfShares: stepForms.shareStructure?.totalNumberOfShares ?? null,
            shareClasses: (stepForms.shareStructure?.shareClasses ?? []).map((sc) => ({
              class: sc.class ?? null,
              numberOfShares: sc.numberOfShares ?? null,
              shareholders: sc.shareholders?.length
                ? sc.shareholders.map((sh) => ({
                    type: sh.type ?? null,
                    email: sh.email?.trim() ?? null,
                    fullName: sh.fullName?.trim() ?? null,
                    numberOfShares: sh.numberOfShares ?? null,
                  }))
                : null,
            })),
          },
        },
      });
      saveCurrentState();
      return stepForms.id;
    } else {
      const result = await postEntities(createEntityData);
      const newId = result?.id;
      if (newId) {
        updateStepForm({ id: newId });
      }
      saveCurrentState();
      return newId;
    }
  }, [hasUnsavedChanges, stepForms, putEntities, postEntities, updateStepForm, saveCurrentState]);

  const isAllStepsValid = Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).every((step) =>
    step === 1 ? validateStep(step, stepForms, regionSelectOptions) : validateStep(step, stepForms),
  );

  useEffect(() => {
    if (!currentEntity) return;

    updateStepForm({
      ...currentEntity,
      registeredAt: currentEntity.registeredAt
        ? new Date(currentEntity.registeredAt as string)
        : null,
      shareStructure: currentEntity.shareStructure ?? stepForms.shareStructure,
      id: currentEntity.id,
    });
    saveCurrentState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateStepForm, currentEntity, saveCurrentState]);

  useEffect(() => {
    if (!currentEntity) return;

    const approved = (publishedEntity as unknown as StepForms) ?? null;
    const { changedPaths, changedCountByStep } = computeEntityDiff(
      approved,
      currentEntity as unknown as StepForms,
    );

    setEntityVersions({
      approved,
      isPendingReview: Boolean(approved) && isReapproval,
      changedFieldPaths: changedPaths,
      changedCountByStep,
      changedDirectorIds: Array.from(
        changedDirectorUserIds(
          currentEntity.directorsUsersIdsApproved ?? [],
          currentEntity.directorsUsersIdsInDraft ?? [],
        ),
      ),
      approvedDirectorUserIds: currentEntity.directorsUsersIdsApproved ?? [],
    });
  }, [currentEntity, publishedEntity, isReapproval, setEntityVersions]);

  useEffect(() => {
    if (params.id && params.id !== stepForms.id) {
      updateStepForm({ id: params.id });
    }
  }, [params.id, stepForms.id, updateStepForm]);

  useEffect(() => {
    if (params.id) {
      isNavigatingToCreatedEntity = false;
      return;
    }
    if (isNavigatingToCreatedEntity) return;
    resetState();
    saveCurrentState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  useEffect(() => {
    if (stepForms.id && !hasUnsavedChanges()) {
      setTimeout(() => saveCurrentState(), 0);
    }
  }, [stepForms.id, hasUnsavedChanges, saveCurrentState]);

  // Keep a ref to latest form data for the beforeunload handler
  const stepFormsRef = useRef(stepForms);
  useEffect(() => {
    stepFormsRef.current = stepForms;
  });

  // Persist entity data before page unload
  useEffect(() => {
    const handler = () => {
      if (isReviewMode) return;
      if (!hasUnsavedChanges()) return;
      if (hasInvalidContactFormat(stepFormsRef.current)) return;

      if (params.id) {
        // Existing entity: fire-and-forget PUT (keepalive: true ensures delivery)
        const data = stepFormsRef.current;
        putEntities({
          id: params.id,
          body: {
            legalName: data.legalName || null,
            displayName: data.displayName || null,
            country: data.country || null,
            state: data.state || null,
            type: data.type || null,
            identificationNumber: data.identificationNumber || null,
            logoImageId: data.logoImageId || COVER_IMAGE_ID,
            registeredAt: data.registeredAt ? (data.registeredAt as Date).toISOString() : null,
            entityEmail: data.entityEmail?.trim() || null,
            entityWebsite: data.entityWebsite?.trim() || null,
            registeredAddressCity: data.registeredAddressCity || null,
            registeredAddress: data.registeredAddress || null,
            registeredAddressPostalCode: data.registeredAddressPostalCode || null,
            shareStructure: {
              totalNumberOfShares: data.shareStructure?.totalNumberOfShares ?? null,
              shareClasses: (data.shareStructure?.shareClasses ?? []).map((sc) => ({
                class: sc.class ?? null,
                numberOfShares: sc.numberOfShares ?? null,
                shareholders: sc.shareholders?.length
                  ? sc.shareholders.map((sh) => ({
                      type: sh.type ?? null,
                      email: sh.email?.trim() ?? null,
                      fullName: sh.fullName?.trim() ?? null,
                      numberOfShares: sh.numberOfShares ?? null,
                    }))
                  : null,
              })),
            },
          },
        }).catch(console.error);
      } else {
        // New entity: save to sessionStorage for creation on remount
        try {
          sessionStorage.setItem(UNSAVED_ENTITY_DRAFT_KEY, JSON.stringify(stepFormsRef.current));
        } catch {
          /* storage unavailable */
        }
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [params.id, hasUnsavedChanges, putEntities, isReviewMode]);

  // On mount: create entity from saved draft and redirect to its page
  useEffect(() => {
    if (params.id) return;

    const savedDraft = sessionStorage.getItem(UNSAVED_ENTITY_DRAFT_KEY);
    sessionStorage.removeItem(UNSAVED_ENTITY_DRAFT_KEY);
    if (!savedDraft) return;

    try {
      const data = JSON.parse(savedDraft);
      postEntities({
        legalName: data.legalName || null,
        displayName: data.displayName || null,
        country: data.country || null,
        state: data.state || null,
        type: data.type || null,
        identificationNumber: data.identificationNumber || null,
        logoImageId: data.logoImageId || COVER_IMAGE_ID,
        registeredAt: data.registeredAt || null,
        entityEmail: data.entityEmail?.trim() || null,
        entityWebsite: data.entityWebsite?.trim() || null,
        registeredAddressCity: data.registeredAddressCity || null,
        registeredAddress: data.registeredAddress || null,
        registeredAddressPostalCode: data.registeredAddressPostalCode || null,
      })
        .then((result) => {
          if (result?.id) {
            navigate(generatePath(`${PagePaths.createEntity}/${result.id}`), { replace: true });
          }
        })
        .catch(console.error);
    } catch {
      /* ignore parse errors */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    currentStep,
    resetState,
    backHandlerClick,
    nextHandlerClick,
    isFirstStep,
    isLastStep,
    setStep,
    countrySelectOptions,
    regionSelectOptions,
    isLoadingRegions,
    isAllStepsValid,
    totalAllocated,
    uploadFile,
    hasUnsavedChanges,
    submit,
    isEntityApprovalStatusDraft,
    isEntityApprovalStatusVerified,
    entityApprovalStatus: currentEntity?.entityApprovalStatus,
    filteredDirectors,
    isStepTransitioning,
    handleStepClick,
    autoSaveOnLeave,
    isReviewMode,
    isOnReviewRoute,
    shouldLeaveReviewRoute,
  };
};
